import { Component } from "react"

/* Redux */
import { connect } from "react-redux"

/* Components */
import { InnerLayout, Pagination } from "../../components"

/* Widgets */
import { OperatorForm, OperatorToken } from "../../widgets/Operator"

/* Translate */
import { t } from "../../translates/translate"

/* REST API */
import { list, remove } from "../../api/Operator"

/* Helpers */
import { withRouter, utils } from "../../helpers"

/* Icons */
import { PlusIcon, EditIcon, TrashIcon, SearchIcon, CloseIcon, LockIcon, KeyIcon, TableIcon, ManagerIcon, LicenseIcon, OperatorIcon } from "../../widgets/Operator/OperatorIcons"

/* Sweet alert */
import Swal from "../../helpers/alert"


/* DELAY BEFORE THE SEARCH REQUEST LEAVES */
const DELAY = 400


/* INITIALS OF A NAME — THE AVATAR DRAWS THEM INSTEAD OF A LOGO */
const getInitials = (name) => {

    /* Split Words */
    const words = String(name).trim().split(" ").filter((item) => item !== "")

    /* Check Words */
    if (words.length === 0) {
        return "?"
    }

    /* Send Result */
    return words.slice(0, 2).map((item) => item.charAt(0).toUpperCase()).join("")
}


/* HOST OF THE STARTPOINT — THE ROW SHOWS THE ADDRESS WITHOUT THE SCHEME */
const getHost = (startpoint) => String(utils.checkValue(startpoint, "")).replace(/^https?:\/\//i, "").replace(/\/+$/, "")


/* Page Operators */
class Operators extends Component {

    constructor() {
        super()

        this.state = {
            data: [],
            status: "loading",
            count: 0,
            search: "",
            canEdit: false,
        }
    }

    componentDidMount = () => {
        window.scrollTo(0, 0)
        this.load()
    }

    componentDidUpdate = (prevProps) => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page — a new page starts from the top with the loader */
        if (prevProps.params.page !== page) {
            window.scrollTo(0, 0)
            this.load(true)
        }

    }

    componentWillUnmount = () => {
        clearTimeout(this._timer)
    }

    /* Get Page */
    getPage = () => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page */
        if (Number.isInteger(parseInt(page))) {
            return parseInt(page)
        }

        return 1
    }

    /* Load Action */
    load = (reload = false) => {

        /* Fields */
        const { search } = this.state
        const page = this.getPage()

        /* Check Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* Send to REST API */
        list(page, search).then((response) => {
            if (response.status === 200) {
                this.setState({ status: "ok", data: response.data.list, count: response.data.count, canEdit: utils.checkBoolean(response.data.canEdit) })
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission", data: [], count: 0 })
            }
            else if (response.status === 498) {
                this.setState({ status: "key", data: [], count: 0 })
            }
            else {
                this.setState({ status: "error", data: [], count: 0 })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [], count: 0 })
        })

    }

    /* Change Search — the request leaves only after the typing stops */
    changeSearch = (search) => {

        this.setState({ search })

        clearTimeout(this._timer)
        this._timer = setTimeout(() => this.search(), DELAY)
    }

    /* Search Action — every search restarts from the first page */
    search = () => {

        /* Fields */
        const { navigate } = this.props

        /* Check Page */
        if (this.getPage() !== 1) {
            navigate("/operator/1")
            return
        }

        this.load()
    }

    /* Remove Action */
    remove = (item) => {

        Swal.fire({
            title: t("swal.deleteConfirm"),
            text: `${item.name} (${item.slug})`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f44336",
            reverseButtons: true,
            confirmButtonText: t("swal.deleteButton"),
            cancelButtonText: t("swal.cancelButton"),
        }).then((result) => {
            if (result.isConfirmed) {
                remove({ id: item.id }).then((response) => {
                    if (response.status === 200) {
                        this.load(true)
                        Swal.fire(utils.notification(t("operator.deletedSuccess"), "success"))
                    }
                    else if (response.data && response.data.message) {
                        Swal.fire(utils.notification(response.data.message, "error"))
                    }
                    else if (response.status === 498) {
                        Swal.fire(utils.notification(t("swal.invalidKey"), "error"))
                    }
                    else if (response.status === 401 || response.status === 403) {
                        Swal.fire(utils.notification(t("swal.noAccess"), "error"))
                    }
                    else {
                        Swal.fire(utils.notification(t("swal.generic"), "error"))
                    }
                }).catch(() => {
                    Swal.fire(utils.notification(t("swal.generic"), "error"))
                })
            }
        })
    }

    /* Draw Search */
    _search = () => {

        /* Fields */
        const { search } = this.state

        return (
            <div className="operator--search">

                <SearchIcon />

                <input
                    value={search}
                    onChange={(event) => this.changeSearch(event.target.value)}
                    placeholder={t("operator.searchByName")}
                />

                {search !== "" &&
                    <div onClick={() => this.changeSearch("")} className="operator--search-clear">
                        <CloseIcon size={12} />
                    </div>
                }

            </div>
        )
    }

    /* Draw Startpoint */
    _startpoint = (item) => {

        /* Fields */
        const host = getHost(item.startpoint)

        /* Check Host */
        if (host === "") {
            return <div className="operator--startpoint-empty">{t("operator.noStartpoint")}</div>
        }

        return (
            <div className="operator--startpoint" title={item.startpoint}>
                <span>{host}</span>
                {item.licenseNumber
                    ? <div className="operator--license" title={t("operator.licenseNumber")}>
                        <LicenseIcon size={11} />
                        <span>{item.licenseNumber}</span>
                    </div>
                    : null
                }
            </div>
        )
    }

    /* Draw Actions */
    _actions = (item) => {

        /* Fields */
        const { canEdit } = this.state

        /* Not A Superadmin — every change is closed */
        if (!canEdit) {
            return (
                <div className="operator--action-locked">
                    <LockIcon />
                    <span>{t("operator.superadminOnly")}</span>
                </div>
            )
        }

        return (
            <>
                <div onClick={() => this._token.open({ id: item.id, name: item.name, slug: item.slug })} className="operator--action" title={t("operator.tokenTitle")}>
                    <KeyIcon size={16} />
                </div>

                <div onClick={() => this._form.open({ type: "edit", id: item.id })} className="operator--action" title={t("operator.edit")}>
                    <EditIcon />
                </div>

                <div onClick={() => this.remove(item)} className="operator--action red" title={t("operator.delete")}>
                    <TrashIcon />
                </div>
            </>
        )
    }

    /* Draw Content */
    _content = () => {

        /* Fields */
        const { status, data, count, search } = this.state
        const { navigate } = this.props
        const page = this.getPage()

        /* Check Status */
        if (status !== "ok") {
            return (
                <div className="operator--page">
                    <InnerLayout inner status={status} reload={() => this.load(true)} />
                </div>
            )
        }

        return (
            <div className="operator--page">

                {/* Head */}
                <div className="operator--table-header">
                    <div className="operator--column number">#</div>
                    <div className="operator--column name">{t("operator.name")}</div>
                    <div className="operator--column startpoint">Startpoint</div>
                    <div className="operator--column tables">{t("operator.tables")}</div>
                    <div className="operator--column managers">{t("operator.managers")}</div>
                    <div className="operator--column actions" />
                </div>

                {/* Empty */}
                {data.length === 0 &&
                    <div className="operator--empty">
                        <OperatorIcon size={40} />
                        <span>{search !== "" ? t("operator.nothingFound") : t("operator.empty")}</span>
                    </div>
                }

                {/* Body */}
                {data.map((item, index) =>
                    <div className="operator--row" key={`${index}`}>

                        <div className="operator--column number">{(page - 1) * 10 + index + 1}</div>

                        <div className="operator--column name">

                            <div className="operator--avatar">{getInitials(item.name)}</div>

                            <div className="operator--person-text">
                                <div className="operator--person-name">{item.name}</div>
                                <div className="operator--person-slug">{item.slug}</div>
                            </div>

                        </div>

                        <div className="operator--column startpoint">{this._startpoint(item)}</div>

                        <div className="operator--column tables">
                            <div className={`operator--tables ${parseInt(item.tablecount) === 0 ? "empty" : ""}`}>
                                <TableIcon />
                                <span>{item.tablecount}</span>
                            </div>
                        </div>

                        <div className="operator--column managers">
                            <div className={`operator--managers ${parseInt(item.managercount) === 0 ? "empty" : ""}`}>
                                <ManagerIcon />
                                <span>{item.managercount}</span>
                            </div>
                        </div>

                        <div className="operator--column actions">{this._actions(item)}</div>

                    </div>
                )}

                {/* Pagination */}
                <Pagination size={10} page={page} total={count} onPageChange={(page) => navigate(`/operator/${page}`)} />

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { status, canEdit } = this.state

        return (
            <div className="page">

                <div className="page-header operator--header">

                    <h1>{t("operator.title")}</h1>

                    {status === "ok" &&
                        <div className="operator--tools">

                            {canEdit &&
                                <div onClick={() => this._form.open({ type: "create" })} className="button operator--add">
                                    <PlusIcon size={14} />
                                    <span>{t("common.add")}</span>
                                </div>
                            }

                            {/* Draw Search */}
                            {this._search()}

                        </div>
                    }

                </div>

                {/* Draw Content */}
                {this._content()}

                {/* Draw Form Modal */}
                <OperatorForm ref={(ref) => this._form = ref} reload={() => this.load()} />

                {/* Draw Token Modal */}
                <OperatorToken ref={(ref) => this._token = ref} />

            </div>
        )
    }

}

const mapStateToProps = (state) => ({
    admin: state.admin,
    language: state.language,
})

export default connect(mapStateToProps)(withRouter(Operators))
